import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { connect } from "./session";
import { INITIAL, type CallState } from "./types";

type Patch = Partial<CallState> | ((s: CallState) => Partial<CallState>);

type Call = {
  state: CallState;
  /** Her loops are fetched on the first call, not with the page. */
  loaded: boolean;
  load: () => void;
  /** Press the button: start, or hang up what is running. */
  press: () => void;
  start: () => void;
  hangUp: () => void;
  /** ?states drives the call by hand; nothing else writes state directly. */
  set: (patch: Patch) => void;
  hush: () => void;
};

const Ctx = createContext<Call | null>(null);

export function CallProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CallState>(INITIAL);
  const [loaded, setLoaded] = useState(false);

  // The running session's stop function — see session.ts.
  const stop = useRef<(() => void) | null>(null);

  const set = useCallback((patch: Patch) => {
    setState((s) => ({ ...s, ...(typeof patch === "function" ? patch(s) : patch) }));
  }, []);

  const hush = useCallback(() => {
    stop.current?.();
    stop.current = null;
  }, []);

  const load = useCallback(() => setLoaded(true), []);

  // The token service scales to zero, and its cold start is most of the
  // couple of seconds the first call of the day spent on Connecting…. One
  // request as the page loads brings it up while the loading screen is
  // still on, and nothing is minted or spent by it. Best effort: a page
  // that cannot reach it finds out on the press, as it always did.
  useEffect(() => {
    void fetch("/api/call/warm", { cache: "no-store" }).catch(() => {});
  }, []);

  // Pressing the button is the whole of starting a call: the browser's own
  // microphone prompt is the gate, and she says what she is in her first line.
  const start = useCallback(() => {
    hush();
    load();
    setState({ ...INITIAL, phase: "connecting" });
    stop.current = connect(set);
  }, [hush, load, set]);

  const hangUp = useCallback(() => {
    hush();
    setState({ ...INITIAL });
  }, [hush]);

  const press = useCallback(() => {
    if (state.phase === "connecting" || state.phase === "live") return hangUp();
    if (state.phase === "ready") start();
  }, [state.phase, hangUp, start]);

  // Nothing keeps ticking once the page is gone.
  useEffect(() => hush, [hush]);

  const value = useMemo<Call>(
    () => ({ state, loaded, load, press, start, hangUp, set, hush }),
    [state, loaded, load, press, start, hangUp, set, hush],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCall(): Call {
  const call = useContext(Ctx);
  if (!call) throw new Error("useCall must be used inside <CallProvider>");
  return call;
}
